import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from '@/components/ui';
import { workIdService } from '../../../services/workIdService';
import { assetService } from '../../../services/assetService';
import { workIdService as featureWorkIdService } from '../workIdService';
import type { WorkIdFilterOptions } from '../workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { Asset } from '../../../types/Asset';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';

interface WorkIdCreateModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WorkIdCreateModal: React.FC<WorkIdCreateModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [foremanAreaId, setForemanAreaId] = useState<number | ''>('');
  const [comments, setComments] = useState('');
  const [priority, setPriority] = useState('');
  const [assetType, setAssetType] = useState<string>(ASSET_TYPES.ROAD);
  const [assetId, setAssetId] = useState<number | ''>('');
  const [filterOptions, setFilterOptions] = useState<WorkIdFilterOptions | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(true);
  const [photos, setPhotos] = useState<FileList | null>(null);

  const selectedForemanArea = filterOptions?.foremanAreas.find(
    (fa) => fa.id === foremanAreaId
  );
  const serviceAreaId = selectedForemanArea?.serviceAreaId;

  const hasLocation = !!location;
  const hasAsset = assetId !== '' && Number(assetId) > 0;
  const hasPriority = !!priority;

  const isValid =
    foremanAreaId !== '' &&
    Number(foremanAreaId) > 0 &&
    typeof comments === 'string' &&
    comments.trim().length > 0 &&
    hasLocation &&
    hasAsset &&
    hasPriority;

  useEffect(() => {
    const load = async () => {
      setOptionsLoading(true);
      try {
        const options = await featureWorkIdService.getFilterOptions();
        setFilterOptions(options);
      } catch (e) {
        console.error('Error loading filter options:', e);
        setFilterOptions(null);
      } finally {
        setOptionsLoading(false);
      }
    };
    if (visible) load();
  }, [visible]);

  useEffect(() => {
    if (!serviceAreaId || !assetType) {
      setAssets([]);
      return;
    }
    const load = async () => {
      setAssetsLoading(true);
      try {
        // Tighten to the selected foreman area (Ashcroft, etc.)
        const data = await assetService.searchAssets({ foremanAreaId: Number(foremanAreaId), assetType });
        setAssets(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error loading assets:', e);
        setAssets([]);
      } finally {
        setAssetsLoading(false);
      }
    };
    load();
  }, [visible, serviceAreaId, foremanAreaId, assetType]);

  useEffect(() => {
    if (!visible) {
      setError(null);
      setForemanAreaId('');
      setComments('');
      setPriority('');
      setAssetType(ASSET_TYPES.ROAD);
      setAssetId('');
      setLocation(null);
      setShowMap(true);
      setPhotos(null);
    }
  }, [visible]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setSubmitting(true);
    setError(null);
    try {
      if (!location) {
        throw new Error('Location is required');
      }
      const payload = {
        foremanAreaId: Number(foremanAreaId),
        comments: comments.trim(),
        priority,
        assetId: Number(assetId),
        latitude: location.lat,
        longitude: location.lng,
      };
      const created = await workIdService.create(payload);

      if (photos && photos.length > 0) {
        const files = Array.from(photos);
        for (const file of files) {
          try {
            await workIdService.uploadAttachment(created.id, file);
          } catch (uploadErr) {
            // Non-blocking: log and continue so Work ID creation still succeeds
            // eslint-disable-next-line no-console
            console.error('Failed to upload work ID photo:', uploadErr);
          }
        }
      }

      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Create Work ID"
      size="large"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Creating…' : 'Create Work ID'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger" className="tw-mb-3">
            {error}
          </Alert>
        )}

        <div className="tw-mb-3">
          <strong className="tw-block tw-mb-2">Required details</strong>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Foreman area <span className="tw-text-danger">*</span>
                </Form.Label>
                {optionsLoading ? (
                  <div className="tw-text-content-muted tw-text-sm">Loading…</div>
                ) : (
                  <Form.Select
                    value={foremanAreaId}
                    onChange={(e) => {
                      setForemanAreaId(e.target.value ? Number(e.target.value) : '');
                      setAssetId('');
                    }}
                    required
                  >
                    <option value="">Select foreman area</option>
                    {filterOptions?.foremanAreas.map((fa) => (
                      <option key={fa.id} value={fa.id}>
                        {fa.name}
                        {fa.serviceAreaName ? ` (${fa.serviceAreaName})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Priority <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="">Select priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="tw-mt-1">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Comments <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Describe the work to be done"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="tw-mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Asset type</Form.Label>
                <Form.Select
                  value={assetType}
                  onChange={(e) => {
                    setAssetType(e.target.value);
                    setAssetId('');
                  }}
                >
                  {(Object.values(ASSET_TYPES) as string[]).map((type) => (
                    <option key={type} value={type}>
                      {ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] ?? type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Asset <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={assetId}
                  onChange={(e) =>
                    setAssetId(e.target.value ? Number(e.target.value) : '')
                  }
                  disabled={!serviceAreaId || assetsLoading}
                >
                  <option value="">
                    {!serviceAreaId
                      ? 'Select a foreman area first'
                      : assetsLoading
                        ? 'Loading assets…'
                        : 'Select asset'}
                  </option>
                  {serviceAreaId && !assetsLoading && assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} (#{a.assetNumber})
                    </option>
                  ))}
                </Form.Select>
                {!hasAsset && (
                  <div className="tw-text-danger tw-text-sm tw-mt-1">
                    Asset is required.
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="tw-mt-3">
            <Form.Label>
              Location / GPS <span className="tw-text-danger">*</span>
            </Form.Label>
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <div className="tw-text-sm tw-text-content-muted">
                Drop a pin or use your current location so crews can see exactly where the work is.
                {location && (
                  <>
                    {' '}
                    <span className="tw-text-content-muted">
                      (Lat {location.lat.toFixed(5)}, Lng {location.lng.toFixed(5)})
                    </span>
                  </>
                )}
              </div>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowMap((prev) => !prev)}
              >
                {showMap ? 'Hide map' : 'Show map'}
              </Button>
            </div>
            {showMap && (
              <GoogleGPSMap
                startLocation={location ?? undefined}
                endLocation={location ?? undefined}
                onLocationSelect={(lat, lng) => setLocation({ lat, lng })}
                locationType="start"
                isPointActivity
                autoFitBounds={false}
                height={260}
              />
            )}
            {!hasLocation && (
              <div className="tw-text-danger tw-text-sm tw-mt-1">
                Location is required. Click on the map or use your current location.
              </div>
            )}
          </div>

          <div className="tw-mt-3">
            <Form.Group>
              <Form.Label>Photos (optional)</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={(e) =>
                  setPhotos((e.target as HTMLInputElement | null)?.files ?? null)
                }
                disabled={submitting}
              />
              <div className="tw-text-content-muted tw-text-sm">
                You can attach one or more photos to help describe the work.
              </div>
            </Form.Group>
          </div>
        </div>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdCreateModal;
